import { Children, createContext, type ReactNode, use } from 'react'
import cx from 'classnames'
import { RawButton } from '@/components/Button'
import {
  Dropdown,
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { Icon, type IconName } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { match } from '@/utils/pattern-match-util'
import { LegendPill } from '@/features/legends/components/LegendPill'
import {
  type SortableItemOptions,
  useSortableItem,
} from '@/features/legends/editor/sortable-hooks'
import styles from './EditorList.module.css'

/**
 * An entry of an {@link EditorList}'s add menu.
 */
export interface EditorListAddOption<T extends string = string> {
  id: T
  title: string
  description?: string
}

export interface EditorListProps<T extends string> {
  title?: string
  iconName?: IconName
  children?: ReactNode
  /**
   * Shows an add button.
   */
  onAdd?: () => unknown
  /**
   * Shows an add menu instead of a button.
   */
  addOptions?: readonly EditorListAddOption<T>[]
  onAddOption?: (id: T) => unknown
  addLabel?: string
  isDisabled?: boolean
  /**
   * Shows the items as separate cards instead of one bordered list.
   */
  isSpaced?: boolean
  testId?: string
}

/**
 * A list of editable items, with a way to add more at the bottom.
 *
 * Not `ListItem` rows: a pressable `ListItem` is one button, so it can't hold
 * a drag handle or remove button, and has no selected, error or muted state.
 */
export function EditorList<T extends string>({
  title,
  iconName,
  children,
  onAdd,
  addOptions,
  onAddOption,
  addLabel = 'Add',
  isDisabled,
  isSpaced,
  testId,
}: EditorListProps<T>) {
  const hasItems = Children.toArray(children).some(Boolean)
  const addButton = (onPress?: () => unknown) => (
    <RawButton
      className={styles.addButton}
      isDisabled={isDisabled}
      onPress={onPress}
      testId={testId && `${testId}-add-button`}
    >
      <Icon name="add" size={14} aria-hidden="true" />
      {addLabel}
    </RawButton>
  )

  return (
    <div
      className={cx(styles.root, isSpaced && styles.spaced)}
      data-test={testId}
    >
      {title && (
        <div className={styles.header}>
          {iconName && <Icon name={iconName} size={20} aria-hidden="true" />}
          <span className={styles.title}>{title}</span>
        </div>
      )}
      {hasItems && <div className={styles.items}>{children}</div>}
      {!isDisabled && onAdd && !addOptions && addButton(onAdd)}
      {!isDisabled && addOptions && onAddOption && (
        <Dropdown autoDismiss position="bottom-start" button={addButton()}>
          {addOptions.map((option) => (
            <DropdownItem
              key={option.id}
              onPress={() => onAddOption(option.id)}
              testId={testId && `${testId}-add-${option.id}`}
            >
              <div className={styles.addOption}>
                <DropdownItemTitle>{option.title}</DropdownItemTitle>
                {option.description && (
                  <DropdownItemDescription>
                    {option.description}
                  </DropdownItemDescription>
                )}
              </div>
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </div>
  )
}

interface PressableRow {
  onPress: () => unknown
  isSelected?: boolean
}

/**
 * Lets a pressable row's content be the button that presses it.
 */
const PressableRowContext = createContext<PressableRow | null>(null)

export interface EditorListItemProps {
  children?: ReactNode
  isSelected?: boolean
  hasError?: boolean
  isMuted?: boolean
  /**
   * Makes the whole row pressable. The row's {@link EditorListItemContent}
   * is the button.
   */
  onPress?: () => unknown
  testId?: string
}

/**
 * A row of an {@link EditorList}.
 */
export function EditorListItem({
  children,
  isSelected,
  hasError,
  isMuted,
  onPress,
  testId,
}: EditorListItemProps) {
  return (
    <div
      className={cx(
        styles.item,
        onPress && styles.pressable,
        isSelected && styles.selected,
        hasError && styles.error,
        isMuted && styles.muted,
      )}
      data-test={testId}
    >
      <PressableRowContext value={onPress ? { onPress, isSelected } : null}>
        {children}
      </PressableRowContext>
    </div>
  )
}

export interface EditorListItemContentProps {
  children?: ReactNode
}

/**
 * The text of a row: a title over a description. In a pressable row, it is
 * the button, and covers the row.
 */
export function EditorListItemContent({
  children,
}: EditorListItemContentProps) {
  const row = use(PressableRowContext)
  if (!row) {
    return <div className={styles.content}>{children}</div>
  }
  return (
    <RawButton
      className={cx(styles.content, styles.pressTarget)}
      aria-current={row.isSelected || undefined}
      onPress={row.onPress}
    >
      {children}
    </RawButton>
  )
}

export interface EditorListItemTitleProps {
  children?: ReactNode
  isMuted?: boolean
}

/**
 * The title of a row.
 */
export function EditorListItemTitle({
  children,
  isMuted,
}: EditorListItemTitleProps) {
  return (
    <span className={cx(styles.itemTitle, isMuted && styles.mutedText)}>
      {children}
    </span>
  )
}

export interface EditorListItemDescriptionProps {
  children?: ReactNode
  isMuted?: boolean
}

/**
 * The description of a row.
 */
export function EditorListItemDescription({
  children,
  isMuted,
}: EditorListItemDescriptionProps) {
  return (
    <span className={cx(styles.itemDescription, isMuted && styles.mutedText)}>
      {children}
    </span>
  )
}

export interface EditorListItemActionsProps {
  children?: ReactNode
}

/**
 * The actions at the end of a row, above the row's button.
 */
export function EditorListItemActions({
  children,
}: EditorListItemActionsProps) {
  return <div className={styles.actions}>{children}</div>
}

export interface EditorListBadgeProps {
  children?: ReactNode
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning'
  testId?: string
}

/**
 * A short label on a row, like an item's kind.
 */
export function EditorListBadge({
  children,
  variant = 'default',
  testId,
}: EditorListBadgeProps) {
  return (
    <LegendPill
      className={match(variant, {
        default: styles.defaultBadge,
        primary: styles.primaryBadge,
        secondary: styles.secondaryBadge,
        success: styles.successBadge,
        warning: styles.warningBadge,
      })}
      testId={testId}
    >
      {children}
    </LegendPill>
  )
}

export interface SortableEditorListItemProps extends Omit<
  SortableItemOptions,
  'isDisabled'
> {
  children?: ReactNode
  isDisabled?: boolean
  /**
   * Shows a header with the label and the remove button above the content.
   */
  label?: string
  onRemove?: () => unknown
  isSelected?: boolean
  hasError?: boolean
  onPress?: () => unknown
  testId?: string
}

/**
 * A row of an {@link EditorList} that can be dragged to reorder.
 */
export function SortableEditorListItem({
  group,
  index,
  onMove,
  isDisabled,
  children,
  label,
  onRemove,
  isSelected,
  hasError,
  onPress,
  testId,
}: SortableEditorListItemProps) {
  const { rowProps, handleProps, isDragging } = useSortableItem({
    group,
    index,
    onMove,
    isDisabled,
  })
  const handle = (
    <RawButton
      className={cx(styles.dragHandle, isDisabled && styles.dragHandleHidden)}
      aria-label="Reorder"
      aria-description="Drag, or press the up and down arrows."
      isDisabled={isDisabled}
      testId={testId && `${testId}-reorder`}
      {...handleProps}
    >
      <Icon name="drag" size={14} aria-hidden="true" />
    </RawButton>
  )

  return (
    <div
      {...rowProps}
      className={cx(
        styles.item,
        styles.sortable,
        label && styles.stacked,
        isDragging && styles.dragging,
        onPress && styles.pressable,
        isSelected && styles.selected,
        hasError && styles.error,
      )}
      data-test={testId}
    >
      <PressableRowContext value={onPress ? { onPress, isSelected } : null}>
        {label ? (
          <>
            <div className={styles.sortableHeader}>
              {handle}
              <span className={styles.sortableLabel}>{label}</span>
              {onRemove && !isDisabled && (
                <IconButton
                  name="close"
                  label="Remove"
                  size={14}
                  onPress={onRemove}
                  testId={testId && `${testId}-remove-button`}
                />
              )}
            </div>
            <div className={styles.sortableBody}>{children}</div>
          </>
        ) : (
          <div className={styles.sortableRow}>
            {handle}
            <div className={styles.sortableContent}>{children}</div>
          </div>
        )}
      </PressableRowContext>
    </div>
  )
}
